import { Box } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useWindowSize } from '@react-hook/window-size';
import { ChipHeatmap } from '@resistapp/client/components/plots/chip-heatmap';
import { SimpleLineChart } from '@resistapp/client/components/plots/simple-line-chart';
import { theme } from '@resistapp/client/components/shared/theme';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useContainerWidth } from '@resistapp/client/hooks/use-container-width';
import { useMemo, useRef } from 'react';
import { QaLabelBox } from './qa-label-box';
import { ChipWarningPanel } from './chip-warning-panel';
import { getChipWarningPanelPlacement } from './chip-warning-utils';
import { useChipContext } from './use-chip-context';

const HIDDEN_MAX_MELTING_TEMPERATURE = 100;
const ASSAY_REFERENCE_LINE_STYLE = {
  strokeWidth: 2,
  strokeOpacity: 1,
};
const MIN_TEMPERATURE_REFERENCE_LINE_STYLE = {
  color: theme.colors.blue700 as string,
  ...ASSAY_REFERENCE_LINE_STYLE,
};
const MAX_TEMPERATURE_REFERENCE_LINE_STYLE = {
  color: theme.colors.red900 as string,
  ...ASSAY_REFERENCE_LINE_STYLE,
};
const LOQ_REFERENCE_LINE_STYLE = {
  color: theme.colors.neutral500 as string,
  ...ASSAY_REFERENCE_LINE_STYLE,
};
const STANDARD_CURVE_TM_BAND_STYLE = {
  fill: theme.colors.neutral500 as string,
  fillOpacity: 0.18,
};

export function ChipViewCharts() {
  const { chipInfo, heatmapData, selectedSamples, selectedGroups, ampCurves, derCurves, showRejected, warnings } =
    useChipContext();
  const { getAssayInfo } = useAssayContext();
  const graphColumnRef = useRef<HTMLDivElement | null>(null);
  const graphColumnWidth = useContainerWidth(graphColumnRef);
  const [, windowHeight] = useWindowSize();

  const derivativeReferenceLines = useMemo(() => {
    if (selectedGroups?.length !== 1) {
      return [];
    }

    const assayInfo = getAssayInfo(selectedGroups[0]);
    if (!assayInfo) {
      return [];
    }

    return [
      assayInfo.minMeltingTemperature != null
        ? {
            id: `${selectedGroups[0]}-tm-min`,
            x: assayInfo.minMeltingTemperature,
            ...MIN_TEMPERATURE_REFERENCE_LINE_STYLE,
          }
        : null,
      assayInfo.maxMeltingTemperature != null && assayInfo.maxMeltingTemperature !== HIDDEN_MAX_MELTING_TEMPERATURE
        ? {
            id: `${selectedGroups[0]}-tm-max`,
            x: assayInfo.maxMeltingTemperature,
            ...MAX_TEMPERATURE_REFERENCE_LINE_STYLE,
          }
        : null,
    ].filter(line => line != null);
  }, [getAssayInfo, selectedGroups]);

  const derivativeReferenceBands = useMemo(() => {
    if (selectedGroups?.length !== 1) {
      return [];
    }

    const assayInfo = getAssayInfo(selectedGroups[0]);
    if (!assayInfo || assayInfo.standardCurveMinTm == null || assayInfo.standardCurveMaxTm == null) {
      return [];
    }

    return [
      {
        id: `${selectedGroups[0]}-standard-curve-tm-range`,
        xMin: assayInfo.standardCurveMinTm,
        xMax: assayInfo.standardCurveMaxTm,
        ...STANDARD_CURVE_TM_BAND_STYLE,
      },
    ];
  }, [getAssayInfo, selectedGroups]);

  const amplificationReferenceLines = useMemo(() => {
    if (selectedGroups?.length !== 1) {
      return [];
    }

    const assayInfo = getAssayInfo(selectedGroups[0]);
    if (!assayInfo || assayInfo.loqCq == null) {
      return [];
    }

    return [{ id: `${selectedGroups[0]}-loq`, x: assayInfo.loqCq, ...LOQ_REFERENCE_LINE_STYLE }];
  }, [getAssayInfo, selectedGroups]);

  // Fallback width if measurement not yet available
  const effectiveGraphWidth = Math.max(100, (graphColumnWidth ?? 0) - 1);

  // Heatmap should be a square whose side length is the smaller of:
  //  1) 75% of the viewport height, and
  //  2) 75% of the available graph column width.
  const heatmapSide = Math.max(100, Math.min(windowHeight * 0.75, effectiveGraphWidth * 0.75));
  const warningPanelPlacement =
    warnings.length === 0 ? null : getChipWarningPanelPlacement(effectiveGraphWidth, heatmapSide);

  return (
    <Box flexGrow={1} minWidth={0} ref={graphColumnRef}>
      <GraphContainer>
        {warningPanelPlacement === 'side' ? (
          <Box display="flex" alignItems="flex-start" gap={6}>
            <Box flex={`0 0 ${heatmapSide}px`}>
              <ChipHeatmap
                data={heatmapData}
                width={heatmapSide}
                height={heatmapSide}
                selectedSamples={selectedSamples}
                selectedAssays={selectedGroups}
                showRejected={showRejected}
              />
            </Box>
            <Box flexGrow={1} minWidth={0}>
              <ChipWarningPanel />
            </Box>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={4}>
            {warningPanelPlacement === 'top' && <ChipWarningPanel />}
            <Box display="flex" justifyContent="center">
              <ChipHeatmap
                data={heatmapData}
                width={heatmapSide}
                height={heatmapSide}
                selectedSamples={selectedSamples}
                selectedAssays={selectedGroups}
                showRejected={showRejected}
              />
            </Box>
          </Box>
        )}
      </GraphContainer>
      <Box height="32px" />
      <GraphContainer>
        <SimpleLineChart
          curves={ampCurves}
          width={effectiveGraphWidth}
          height={300}
          showRejected={showRejected}
          verticalReferenceLines={amplificationReferenceLines}
        />
      </GraphContainer>
      <Box height="32px" />
      <GraphContainer>
        <SimpleLineChart
          curves={derCurves}
          width={effectiveGraphWidth}
          height={300}
          showRejected={showRejected}
          verticalReferenceLines={derivativeReferenceLines}
          verticalReferenceBands={derivativeReferenceBands}
        />
      </GraphContainer>
      <Box mt={4}>
        <QaLabelBox key={chipInfo?.id ?? 'qa-label-box'} />
      </Box>
    </Box>
  );
}

const GraphContainer = styled(Box)`
  /* Container solely provides spacing; border & background are rendered by chart */
  padding: 0;
  overflow: visible;
`;
